import { motion } from "framer-motion";
import { Globe, Heart, Shield, Star, Users } from "lucide-react";
import { StoreButtons } from "@/components/landing/StoreButtons";
import { PageHero } from "@/components/layout/PageHero";
import { pages } from "@/content/pages";
import type { Locale } from "@/i18n/locales";

type CommunityViewProps = {
  locale: Locale;
};

const icons = [Shield, Heart, Globe, Users];
const communityPhotos = [
  "/images/testimonials/lena.webp",
  "/images/testimonials/ayaan.webp",
  "/images/testimonials/sophie.webp",
];

export function CommunityView({ locale }: CommunityViewProps) {
  const copy = pages[locale].community;

  return (
    <main className="bg-background">
      <PageHero eyebrow={copy.eyebrow} title={copy.title} intro={copy.intro}>
        <StoreButtons locale={locale} className="mt-8 justify-center" />
        <div className="mt-10 flex items-center justify-center gap-3 text-sm text-muted-foreground">
          <div className="flex -space-x-2">
            {communityPhotos.map((src) => (
              <img
                key={src}
                src={src}
                alt=""
                width={320}
                height={320}
                loading="lazy"
                className="h-8 w-8 rounded-full border-2 border-background object-cover"
              />
            ))}
          </div>
          <span>{copy.proof}</span>
        </div>
      </PageHero>

      <section className="bg-secondary/40 py-20">
        <div className="mx-auto max-w-6xl px-5">
          <div className="mb-14 text-center">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">
              {copy.valuesEyebrow}
            </span>
            <h2 className="mt-4 font-heading text-3xl font-bold text-[#1a3a5c] md:text-4xl">
              {copy.valuesTitle}
            </h2>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {copy.values.map((value, index) => {
              const Icon = icons[index] ?? Shield;
              return (
                <motion.article
                  key={value.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="rounded-2xl border border-border/60 bg-card p-7 transition-all hover:border-primary/30 hover:shadow-md"
                >
                  <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="mb-2 font-heading text-base font-semibold text-[#1a3a5c]">
                    {value.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {value.description}
                  </p>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-5xl px-5">
          <div className="mb-14 text-center">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">
              {copy.storiesEyebrow}
            </span>
            <h2 className="mt-4 font-heading text-3xl font-bold text-[#1a3a5c] md:text-4xl">
              {copy.storiesTitle}
            </h2>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {copy.testimonials.map((item, index) => (
              <motion.article
                key={item.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.12 }}
                className="flex flex-col gap-4 rounded-2xl border border-border/60 bg-card p-7"
              >
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className="h-4 w-4 fill-accent text-accent" />
                  ))}
                </div>
                <p className="flex-1 text-sm leading-relaxed text-muted-foreground">
                  “{item.quote}”
                </p>
                <div className="flex items-center gap-3">
                  <img
                    src={communityPhotos[index]}
                    alt=""
                    width={320}
                    height={320}
                    loading="lazy"
                    className="h-10 w-10 rounded-full object-cover"
                  />
                  <div>
                    <div className="text-sm font-semibold text-foreground">
                      {item.name}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {item.role}
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#1a3a5c] py-20">
        <div className="mx-auto max-w-2xl px-5 text-center">
          <Heart className="mx-auto mb-6 h-10 w-10 text-accent" />
          <h2 className="mb-4 font-heading text-3xl font-bold text-white md:text-4xl">
            {copy.movementTitle}
          </h2>
          <p className="mb-8 text-lg leading-relaxed text-white/70">
            {copy.movementText}
          </p>
          <StoreButtons locale={locale} className="justify-center" />
        </div>
      </section>
    </main>
  );
}
